import { z } from 'zod'

export const shareResourceTypeSchema = z.enum(['file', 'folder'])

/** How a public share is gated. */
export const shareAuthTypeSchema = z.enum(['public', 'password', 'email', 'sso'])

export type ShareAuthType = z.output<typeof shareAuthTypeSchema>

/** An allowed email address or `@domain` pattern for email/SSO shares. */
const allowedEmailSchema = z.string().min(1).max(320)

/** Password accepted when a file share is created or its password is changed. */
export const sharePasswordSchema = z
  .string()
  .min(15, 'Password must be at least 15 characters')
  .max(1024, 'Password is too long')

/**
 * Public-safe representation of a `public_share` row. Never carries the
 * underlying storage key or the (encrypted) password — `hasPassword` is the
 * only password signal exposed to clients. `allowedEmails` is the allow-list for
 * email/SSO shares (visible only to workspace members via the authed share route).
 */
export const shareRecordSchema = z.object({
  id: z.string().describe('Unique share identifier.'),
  token: z.string().describe('Server-generated token embedded in the public share URL.'),
  url: z.string().describe('Public share URL.'),
  isActive: z.boolean().describe('Whether the public share currently resolves.'),
  resourceType: shareResourceTypeSchema.describe('Kind of resource being shared.'),
  resourceId: z.string().describe('Identifier of the shared resource.'),
  authType: shareAuthTypeSchema.describe('How access to the share is gated.'),
  hasPassword: z.boolean().describe('Whether a password is stored for this share.'),
  allowedEmails: z
    .array(allowedEmailSchema)
    .describe('Allowed addresses or @domain patterns for email and SSO shares.'),
})

export type ShareRecord = z.output<typeof shareRecordSchema>
